import {
  EditService,
  type EditImageRequest,
  type ImageRequest,
} from '@imagise/generated-imagise-cloud-api-client';
import type { Observable } from 'rxjs';

export type NormalizedPayloadValue =
  | string
  | number
  | boolean
  | null
  | NormalizedPayload
  | Array<NormalizedPayloadValue>;

export interface NormalizedPayload {
  [key: string]: NormalizedPayloadValue | undefined;
}

export interface NormalizedImageRequestPayload extends NormalizedPayload {
  $type?: string;

  ToFormat?: string | null;

  ImageBase64Files?: Array<string>;
}

export interface NormalizedEditImageRequestPayload
  extends NormalizedImageRequestPayload
{
  Edits?: Array<NormalizedImageRequestPayload>;
}

/** Wire metadata attached to polymorphic edit steps before PascalCase serialization. */
export type EditWireDiscriminator = {
  $type: string;
};

/** Generated edit step plus polymorphic `$type` metadata before wire serialization. */
export type WireEditStep<T extends ImageRequest = ImageRequest> = T & EditWireDiscriminator;

export type EditWireImageRequest = WireEditStep;

export type EditWireRequest = Omit<EditImageRequest, 'edits'> & {
  edits?: Array<WireEditStep>;
};

// Generated OpenAPI types are camelCase; /edit still expects PascalCase on the wire.
export function serializeEditImageRequest(
  request: EditImageRequest | EditWireRequest,
): NormalizedEditImageRequestPayload {
  return normalizeRequestPayload(request) as NormalizedEditImageRequestPayload;
}

/** POST a built edit request through the generated client after PascalCase wire serialization. */
export function dispatchEditImageRequest(
  editService: EditService,
  request: EditImageRequest | EditWireRequest,
): Observable<Blob> {
  return editService.edit(serializeEditImageRequest(request) as EditImageRequest);
}

function normalizeRequestPayload(request: object): NormalizedPayload {
  const normalized = normalizePayloadValue(request);

  if (!isNormalizedPayload(normalized)) {
    throw new Error('Request payload must be an object.');
  }

  return normalized;
}

function normalizePayloadValue(value: unknown): NormalizedPayloadValue | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    return value
      .map((item) => normalizePayloadValue(item))
      .filter((item): item is NormalizedPayloadValue => item !== undefined);
  }

  if (typeof value === 'object') {
    return normalizeObjectPayload(value as Record<string, unknown>);
  }

  return undefined;
}

function normalizeObjectPayload(source: Record<string, unknown>): NormalizedPayload {
  const normalized: NormalizedPayload = {};

  // The "$type" polymorphism discriminator MUST be serialized first:
  // Newtonsoft (without MetadataPropertyHandling.ReadAhead) and System.Text.Json
  // only honor it as the leading property of the object.
  const typeDiscriminator = source['$type'];
  if (typeof typeDiscriminator === 'string') {
    normalized['$type'] = typeDiscriminator;
  }

  const toFormat = resolveToFormat(source);
  if (toFormat !== undefined) {
    normalized['ToFormat'] = toFormat;
  }

  for (const [key, rawValue] of Object.entries(source)) {
    if (isReservedSourceKey(key)) {
      continue;
    }

    const normalizedValue = normalizePayloadValue(rawValue);
    if (normalizedValue !== undefined) {
      normalized[toWirePropertyName(key)] = normalizedValue;
    }
  }

  return normalized;
}

function isReservedSourceKey(key: string): boolean {
  const wireKey = toWirePropertyName(key);
  return wireKey === '$type' || wireKey === 'ToFormat';
}

function resolveToFormat(source: Record<string, unknown>): string | null | undefined {
  for (const key of ['ToFormat', 'toFormat', 'TtoFormat', 'ttoFormat']) {
    if (isNullableString(source[key])) {
      return source[key];
    }
  }

  return undefined;
}

function toWirePropertyName(key: string): string {
  if (key === '$type') {
    return '$type';
  }

  if (key === 'TtoFormat' || key === 'ttoFormat' || key === 'toFormat' || key === 'ToFormat') {
    return 'ToFormat';
  }

  if (key === 'Edits' || key === 'edits') {
    return 'Edits';
  }

  if (key === 'ImageBase64Files' || key === 'imageBase64Files') {
    return 'ImageBase64Files';
  }

  if (/^[A-Z]/.test(key)) {
    return key;
  }

  return key.charAt(0).toUpperCase() + key.slice(1);
}

function isNullableString(value: unknown): value is string | null {
  return typeof value === 'string' || value === null;
}

function isNormalizedPayload(value: unknown): value is NormalizedPayload {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
